// Pure helpers behind the posting location grid (ForecastLocationGrid.vue).
// Kept out of the SFC so they can be unit tested without mounting ag-grid.
//
// Grades are handled in metric here : the grid converts them (imperial projects) in its
// valueGetters, after the weighted averages have been computed.

export function findGrade(results, elementId) {
  if (!Array.isArray(results)) {
    return null;
  }

  const result = results.find(r => r.element === elementId);
  return result && result.grade !== null && result.grade !== undefined ? result.grade : null;
}

// Posted locations keep the values frozen when they were posted, the recalculated ones can
// have moved since (new mucks, new samples, muck type factor change). A location posted before
// the snapshot was exposed by the API falls back to the recalculated values.
export function getActualGrade(data, elementId) {
  if (data.isPosted) {
    const postedGrade = findGrade(data.postedResults, elementId);
    if (postedGrade !== null) {
      return postedGrade;
    }
  }

  return findGrade(data.sampleOrForecastCappedResults, elementId);
}

// The posting snapshot holds no density (PostedLocationForecast only stores the tonnages and
// the grades), so a posted location keeps the density of its forecast, which is what the posting
// summary report displays.
export function getDensity(data) {
  return data.forecastDensity === null || data.forecastDensity === undefined ? null : data.forecastDensity;
}

export function getActualTonnage(data) {
  return data.isPosted && data.postedTonnage !== null && data.postedTonnage !== undefined
    ? data.postedTonnage
    : data.actualTonnage;
}

export function getForecastTonnage(data) {
  return data.isPosted && data.postedForecastTonnage !== null && data.postedForecastTonnage !== undefined
    ? data.postedForecastTonnage
    : data.forecastTonnage;
}

// Tonnage weighted average, used for the grades and for the density. A row without value is
// left out of both terms instead of being counted as a zero.
export function getTonnageWeightedAverage(rows, valueAccessor, weightAccessor) {
  let amount = 0;
  let weight = 0;

  rows.forEach(row => {
    const value = valueAccessor(row);
    if (value === null) {
      return;
    }
    const rowWeight = weightAccessor(row);
    amount += value * rowWeight;
    weight += rowWeight;
  });

  return weight === 0 ? null : amount / weight;
}

// Row pinned at the bottom of the grid : tonnages are summed, grades and density are tonnage
// weighted averages, same aggregation as the posting summary report. It is shaped like a stats
// row so the column valueGetters work on it unchanged.
export function buildTotalRow(rows, elementIds, totalLabel) {
  if (!rows || rows.length === 0) {
    return [];
  }

  const total = {
    locationForecastId: -1,
    locationCode: totalLabel,
    locationType: null,
    isPosted: false,
    actualTonnage: 0,
    forecastTonnage: 0,
    forecastDensity: null,
    receivedAuSamples: 0,
    receivedBaseSamples: 0,
    totalSamples: 0,
    sampleOrForecastCappedResults: [],
    forecastResults: [],
    postedResults: []
  };

  rows.forEach(row => {
    total.actualTonnage += getActualTonnage(row);
    total.forecastTonnage += getForecastTonnage(row);
    total.receivedAuSamples += row.receivedAuSamples;
    total.receivedBaseSamples += row.receivedBaseSamples;
    total.totalSamples += row.totalSamples;
  });

  total.forecastDensity = getTonnageWeightedAverage(rows, getDensity, getActualTonnage);
  total.sampleReceivedAuRate = total.totalSamples === 0 ? 0 : total.receivedAuSamples / total.totalSamples * 100;
  total.sampleReceivedBaseRate = total.totalSamples === 0 ? 0 : total.receivedBaseSamples / total.totalSamples * 100;

  elementIds.forEach(elementId => {
    const actualGrade = getTonnageWeightedAverage(rows, row => getActualGrade(row, elementId), getActualTonnage);
    if (actualGrade !== null) {
      total.sampleOrForecastCappedResults.push({ element: elementId, grade: actualGrade });
    }
  });

  return [total];
}
